import React, { useState } from 'react';
import { Form, Icon, Popup, Segment, TextArea } from 'semantic-ui-react';
import { FileInput, Notification, STATUS_TYPES, Button, FileUtils, FILE_CATEGORIES } from 'dyl-components';

import { Controller, useController, useFormContext } from 'react-hook-form';

import './index.scss';

const TEXT_CHARACTER_LIMIT = 160;

const OptOutField = ({ optOutFieldName, disabled }) => {
    const { control } = useFormContext();
    const { field } = useController({
        control,
        name: optOutFieldName
    });
    return (
        <Form.Checkbox
            label='Include opt-out message'
            checked={field.value}
            onChange={(_, { checked }) => field.onChange({ target: { name: field.name, value: checked } })}
            disabled={disabled}
        />
    )
}

const TextForm = ({
    editorRef = React.createRef(),
    dropzoneRef = React.createRef(),
    templateSelector = null,
    actions = null,
    textFieldName = "text",
    attachmentsFieldName = "attachments",
    loading,
    optOutFieldName,
    rows = 13,
    onReplaceVariable = (variable) => { return variable },
    isAccount,
    disabled,
    disableOptOut,
    infoIconMessage,
    additionalCharacters = 0
}) => {
    const [isPersonalizing, setPersonalize] = useState(false);

    const { control, watch } = useFormContext();

    const { field: textField } = useController({
        control,
        name: textFieldName,
    })

    const personalize = (variable) => {
        setPersonalize(false);
        const element = editorRef.current;
        const toInsert = onReplaceVariable(variable);
        if (element.getEditorSelection) {
            const selection = element.getEditorSelection();
            const position = selection ? selection.index : 0;
            element.getEditor().insertText(position, toInsert);
        } else {
            const textarea = element.ref.current;
            textarea.focus();
            const selectionStart = textarea.selectionStart;
            const text = textField.value;
            const beginning = text.substr(0, selectionStart);
            const end = text.substr(selectionStart);
            textField.onChange({ target: { name: textFieldName, value: `${beginning}${toInsert}${end}` } });
        }
    }

    return (
        <Segment.Group className='TextForm'>
            <Controller
                name={textFieldName}
                control={control}
                render={({ field: { onChange, value, name }, }) => (
                    <Segment loading={loading}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {optOutFieldName && (
                                <div className='TextForm__text-optout'>
                                    <OptOutField optOutFieldName={optOutFieldName} disabled={disableOptOut} style={{margin: 0}}/>
                                    <Popup
                                        trigger={<Icon style={{ marginLeft: '0.5em', marginTop: '5px' }} name='fas fa-info-circle' color='primary' disabled={disableOptOut} />}
                                        content={infoIconMessage}
                                        inverted
                                        position='bottom center' disabled={disableOptOut} />
                                </div>
                            )}
                            {actions && (
                                <div style={{ marginLeft: 'auto' }} className='TextForm__actions' disabled={loading}>
                                    {actions}
                                </div>
                            )}
                        </div>
                        <Segment disabled={disabled} basic>
                        <div className='TextForm__text-section'>
                            <Form.Group>
                                {templateSelector && (
                                    <Form.Field control='div' width={4}>
                                        {templateSelector}
                                    </Form.Field>
                                )}
                                <Form.Field control='div' width={4}>
                                    <Popup
                                        trigger={<Button disabled={disabled} onClick={() => { setPersonalize(!isPersonalizing) }} type="reset" primary compact>Personalize</Button>}
                                        open={isPersonalizing}
                                        onClose={() => { setPersonalize(false) }}
                                        content={(
                                            <Form className="TextForm__variables">
                                                <h5>Sender Variables</h5>
                                                <Form.Group widths='equal'>
                                                    <Form.Button onClick={() => { personalize('%%My Full Name%%') }} fluid size='mini' basic primary>My Full Name</Form.Button>
                                                    <Form.Button onClick={() => { personalize('%%My First Name%%') }} fluid size='mini' basic primary>My First Name</Form.Button>
                                                </Form.Group>
                                                <Form.Group widths='equal'>
                                                    <Form.Button onClick={() => { personalize('%%My Phone%%') }} fluid size='mini' basic primary>My Phone</Form.Button>
                                                    <Form.Button onClick={() => { personalize('%%My Company%%') }} fluid size='mini' basic primary>My Company</Form.Button>
                                                </Form.Group>
                                                <h5>Recipient Variables</h5>
                                                <Form.Group widths='equal'>
                                                    <Form.Button onClick={() => { personalize('%%Full Name%%') }} fluid size='mini' basic primary>Full Name</Form.Button>
                                                    {!isAccount &&
                                                        <Form.Button onClick={() => { personalize('%%First Name%%') }} fluid size='mini' basic primary>First Name</Form.Button>
                                                    }
                                                    {isAccount &&
                                                        <Form.Button onClick={() => { personalize('%%Phone%%') }} fluid size='mini' basic primary>Phone</Form.Button>
                                                    }
                                                </Form.Group>
                                                {!isAccount &&
                                                    <Form.Group widths='equal'>
                                                        <Form.Button onClick={() => { personalize('%%Last Name%%') }} fluid size='mini' basic primary>Last Name</Form.Button>
                                                        <Form.Button onClick={() => { personalize('%%Phone%%') }} fluid size='mini' basic primary>Phone</Form.Button>
                                                    </Form.Group>
                                                }
                                                <Form.Group widths='equal'>
                                                    <Form.Button onClick={() => { personalize('%%Address%%') }} fluid size='mini' basic primary>Address</Form.Button>
                                                    <Form.Button onClick={() => { personalize('%%City%%') }} fluid size='mini' basic primary>City</Form.Button>
                                                </Form.Group>
                                                <Form.Group widths='equal'>
                                                    <Form.Button onClick={() => { personalize('%%State%%') }} fluid size='mini' basic primary>State</Form.Button>
                                                    <Form.Button onClick={() => { personalize('%%Zip%%') }} fluid size='mini' basic primary>Zip</Form.Button>
                                                </Form.Group>
                                            </Form>
                                        )}
                                        position='bottom right'
                                        on='click'
                                    />
                                </Form.Field>
                                <Form.Field control='div' width={8} style={{ textAlign: 'right' }}>
                                    <Popup 
                                        offset={[10, 0]}
                                        trigger={<Icon className='fas fa-info-circle' color='primary' />}
                                        content="Text templates may increase the character limit when the message is sent."
                                        inverted
                                        position='bottom right'
                                    />
                                </Form.Field>
                            </Form.Group>
                            <TextArea
                                onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                value={value}
                                className="TextForm__body"
                                ref={editorRef}
                                rows={rows}
                                placeholder="Type a message..."
                                disabled={disabled}
                            />
                            <div className="TextForm__body-character-count">
                                <section><b>Character count:</b> {watch(optOutFieldName) ? value.length + additionalCharacters : value.length}/{TEXT_CHARACTER_LIMIT}</section>
                                <section><em>{TEXT_CHARACTER_LIMIT} characters max per message. Additional charges apply.</em></section>
                            </div>
                        </div>
                        </Segment>
                    </Segment>
                )}
            />
            <Controller
                name={attachmentsFieldName}
                defaultValue={[]}
                control={control}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <Form.Field
                        control={Segment}
                        error={error && error.message && {
                            pointing: 'below'
                        }}
                        className='TextForm__attachments-section'
                        loading={loading}
                        disabled={disabled}
                    >
                        <div>
                            <Icon disabled={disabled} onClick={() => { dropzoneRef.current.open() }} color='blue' name='plus circle' link /> <b>Attach {value.length > 0 && 'more '}files</b>
                        </div>
                        <div className='TextForm__attachments'>
                            <FileInput
                                onChange={(_, { value: new_value }) => {
                                    onChange({
                                        target: {
                                            name, value: [
                                                ...value,
                                                ...new_value.filter(new_file => (
                                                    value.findIndex(file => file.path === new_file.path) === -1
                                                ))
                                            ]
                                        }
                                    })
                                }}
                                onReject={(rejected) => {
                                    if (rejected.length > 0) {
                                        Notification.alert("File type must be .pdf, .png, .jpg, or .jpeg", STATUS_TYPES.ERROR, true);
                                    }
                                }}
                                onRemove={(_, { value }) => { onChange({ target: { name, value } }) }}
                                files={value}
                                name="files"
                                accept="application/pdf, image/png, image/jpeg"
                                icon="file pdf outline"
                                size="mini"
                                dropzoneRef={dropzoneRef}
                                showIcon
                                hasBrowse
                                multiIcon
                            />
                        </div>
                        <i>Remaining: {FileUtils.getRemainingSize(value, FILE_CATEGORIES.TEXT)} MB </i> {error?.message && `(${error.message})`}
                    </Form.Field>
                )}
            />
        </Segment.Group>
    );
};

export default TextForm;
